/**
 * Language storage utilities for managing user language preferences
 * Provides a centralized way to handle language persistence with priority system
 */

const LANGUAGE_STORAGE_KEY = 'sarvalok_user_language';
const I18N_STORAGE_KEY = 'i18nextLng';
const SUPPORTED_LANGS = ["en", "cn", "zh", "de", "es", "pl", "hi", "ta", "te", "ne", "he", "fr", "ja", "pt", "id", "ru", "nl"];
const DEFAULT_LANG = "en";

export interface LanguagePreference {
  language: string;
  isManualSelection: boolean;
  timestamp: number;
}

/**
 * Store user's language preference with metadata
 */
export const storeLanguagePreference = (language: string, isManualSelection: boolean = false): void => {
  if (!SUPPORTED_LANGS.includes(language)) {
    console.warn(`Unsupported language: ${language}. Falling back to ${DEFAULT_LANG}`);
    language = DEFAULT_LANG;
  }

  const preference: LanguagePreference = {
    language,
    isManualSelection,
    timestamp: Date.now()
  };

  try {
    // Store our enhanced preference
    localStorage.setItem(LANGUAGE_STORAGE_KEY, JSON.stringify(preference));
    
    // Also store for i18next compatibility
    localStorage.setItem(I18N_STORAGE_KEY, language);
  } catch (error) {
    console.warn('Failed to store language preference:', error);
  }
};

/**
 * Get stored language preference with priority system
 */
export const getStoredLanguagePreference = (): LanguagePreference | null => {
  try {
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (stored) {
      const preference = JSON.parse(stored) as LanguagePreference;
      
      // Validate the stored preference
      if (preference.language && SUPPORTED_LANGS.includes(preference.language)) {
        return preference;
      }
    }
  } catch (error) {
    console.warn('Failed to parse stored language preference:', error);
  }
  
  return null;
};

/**
 * Get the best language to use based on priority:
 * 1. Manual user selection (stored preference with isManualSelection = true)
 * 2. Previous automatic selection (stored preference with isManualSelection = false)
 * 3. Browser language
 * 4. Default language
 */
export const getBestLanguage = (): string => {
  // Check for stored preference first
  const storedPreference = getStoredLanguagePreference();
  if (storedPreference) {
    return storedPreference.language;
  }

  // Check legacy i18next storage
  const legacyLang = localStorage.getItem(I18N_STORAGE_KEY);
  if (legacyLang && SUPPORTED_LANGS.includes(legacyLang)) {
    // Migrate to new system
    storeLanguagePreference(legacyLang, false);
    return legacyLang;
  }

  // Check browser language with region-aware CN/ZH handling
  const browserLangFull = (
    Array.isArray(navigator.languages) && navigator.languages.length
      ? navigator.languages[0]
      : navigator.language || ''
  ).toLowerCase();

  let resolvedLang: string | null = null;
  if (browserLangFull.startsWith('zh')) {
    // Traditional markers: zh-Hant, zh-TW, zh-HK, zh-MO
    const isTraditional =
      browserLangFull.includes('zh-hant') ||
      browserLangFull.includes('-tw') ||
      browserLangFull.includes('-hk') ||
      browserLangFull.includes('-mo');
    resolvedLang = isTraditional ? 'zh' : 'cn';
  } else {
    const baseLang = browserLangFull.split('-')[0];
    if (SUPPORTED_LANGS.includes(baseLang)) {
      resolvedLang = baseLang;
    }
  }

  if (resolvedLang && SUPPORTED_LANGS.includes(resolvedLang)) {
    // Store as automatic selection
    storeLanguagePreference(resolvedLang, false);
    return resolvedLang;
  }

  // Fall back to default
  storeLanguagePreference(DEFAULT_LANG, false);
  return DEFAULT_LANG;
};

/**
 * Check if current language preference was manually selected
 */
export const isManualLanguageSelection = (): boolean => {
  const preference = getStoredLanguagePreference();
  return preference?.isManualSelection || false;
};

/**
 * Clear language preferences (useful for testing or reset)
 */
export const clearLanguagePreferences = (): void => {
  try {
    localStorage.removeItem(LANGUAGE_STORAGE_KEY);
    localStorage.removeItem(I18N_STORAGE_KEY);
  } catch (error) {
    console.warn('Failed to clear language preferences:', error);
  }
};

export { SUPPORTED_LANGS, DEFAULT_LANG };